import { useEffect, useState } from "react";

import { getAllUsers } from "services/UserService";
import {
  resetFiltersUtenti,
  setIsLoadingUsers,
  setPageUtentiAndSize,
  setSorting,
  setUsers,
} from "store/slicer/GestioneUsersSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  labelAdmin,
  labelCognome,
  labelDi,
  labelElement,
  labelElementsViewed,
  labelEmail,
  labelNome,
  labelNoResults,
  labelRuolo,
  labelSuperAdmin,
  labelUserEmpty,
} from "_constants";
import { EmptyMessageTable, IconSortTable } from "components/shared";
import { Dropdown } from "primereact/dropdown";
import { getRoleName } from "utils/utils";
import { Skeleton } from "primereact/skeleton";
import { handleSort } from "utils";
import { User } from "interfaces";
import { UsersTableRowButtonActions } from "./UsersTableRowButtonActions";
import { ModalEditUser } from "components/modals";
import { Tooltip } from "primereact/tooltip";

export const UsersTable = () => {
  const dispatch = useAppDispatch();
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const filters = useAppSelector((state) => state.gestioneUsersSlice.filters);
  const isLoadingUsers = useAppSelector(
    (state) => state.gestioneUsersSlice.isLoadingUsers
  );
  const userList = useAppSelector((state) => state.gestioneUsersSlice.userList);
  const sortedDirection = useAppSelector(
    (state) => state.gestioneUsersSlice.sortedDirection
  );
  const sortedColumn = useAppSelector(
    (state) => state.gestioneUsersSlice.sortedColumn
  );
  const currentUserRole = useAppSelector((state) => state.profiloSlice.role);

  useEffect(() => {
    return () => {
      dispatch(resetFiltersUtenti());
    };
  }, [dispatch]);

  useEffect(() => {
    if (filters.pageIndex > -1) {
      dispatch(setIsLoadingUsers(true));
      getAllUsers(filters)
        .then((response) => {
          setTotalCount(response.data.totalCount);
          dispatch(setUsers(response.data.items));
        })
        .catch((_) => {})
        .finally(() =>
          setTimeout(() => {
            setFirstLoad(false);
            dispatch(setIsLoadingUsers(false));
          }, 500)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onPage = (event: DataTableStateEvent) => {
    if (event.page || event.rows) {
      dispatch(
        setPageUtentiAndSize({
          page: event.page ? event.page + 1 : 1,
          size: event.rows,
        })
      );
    }
  };

  const callHandleSort = (field: String) => {
    handleSort(sortedColumn, sortedDirection, dispatch, setSorting, field);
  };

  const template2 = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
    RowsPerPageDropdown: (options: any) => {
      const dropdownOptions = [
        { label: 5, value: 5 },
        { label: 10, value: 10 },
        { label: 25, value: 25 },
      ];

      return (
        <Dropdown
          value={options.value}
          options={dropdownOptions}
          onChange={options.onChange}
        />
      );
    },
    CurrentPageReport: (options: any) => {
      const first = (filters.pageIndex - 1) * filters.pageSize + 1;
      const last =
        (filters.pageIndex - 1) * filters.pageSize + filters.pageSize;

      return (
        <>
          <span className="xs-none">
            {labelElementsViewed}
            {first} - {last} {labelDi}
            {totalCount} {labelElement}
            {options.totalRecords > 1 ? "i" : "o"}
          </span>
        </>
      );
    },
  };

  const showEditModalDialog = (user: User) => {
    setUserToEdit(user);
    setShowEditModal(true);
  };

  const closeModalEditTopic = () => {
    setUserToEdit(null);
    setShowEditModal(false);
  };

  const getEmptyMessageTableLabel = () => {
    return isLoadingUsers
      ? ""
      : filters.pageIndex === 1 &&
        !filters.globalSearch?.length &&
        !filters.sorting &&
        !filters.role
      ? labelUserEmpty
      : labelNoResults;
  };

  return (
    <>
      <DataTable
        value={userList}
        size="small"
        lazy
        dataKey="id"
        paginator={userList?.length ? true : false}
        rows={filters.pageSize}
        totalRecords={totalCount}
        scrollable
        scrollHeight={`calc(100vh - ${userList?.length ? "240" : "180"}px)`}
        onPage={onPage}
        first={filters.pageIndex * filters.pageSize - 1}
        paginatorClassName="flex-container justify-end"
        className={`margin-18 custom-table ${
          userList.length ? "has-element" : ""
        }`}
        tableStyle={{ minWidth: userList?.length ? "100%" : "50vw" }}
        emptyMessage={
          !firstLoad ? (
            <EmptyMessageTable label={getEmptyMessageTableLabel()} />
          ) : (
            <div></div>
          )
        }
        paginatorTemplate={template2}
      >
        <Column
          field="name"
          header={
            <IconSortTable
              nomeCampo={labelNome}
              onSortCallback={() =>
                isLoadingUsers ? null : callHandleSort("name")
              }
              iconStatus={
                sortedColumn !== "name"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingUsers ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  <Tooltip
                    target={`${
                      rowData.name.length > 40 ? ".user-name-column" : ""
                    }`}
                    mouseTrack
                    mouseTrackLeft={10}
                  />

                  <div
                    className={`user-name-column ellipsis fit-content`}
                    data-pr-tooltip={rowData.name}
                  >
                    <span className="capitalize">{rowData.name}</span>
                  </div>
                </>
              )
            )
          }
        />
        <Column
          field="lastName"
          header={
            <IconSortTable
              nomeCampo={labelCognome}
              onSortCallback={() =>
                isLoadingUsers ? null : callHandleSort("lastName")
              }
              iconStatus={
                sortedColumn !== "lastName"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingUsers ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  <Tooltip
                    target={`${
                      rowData.lastName.length > 40
                        ? ".user-lastname-column"
                        : ""
                    }`}
                    mouseTrack
                    mouseTrackLeft={10}
                  />

                  <div
                    className={`user-lastname-column ellipsis fit-content`}
                    data-pr-tooltip={rowData.lastName}
                  >
                    <span className="capitalize">{rowData.lastName}</span>
                  </div>
                </>
              )
            )
          }
        />
        <Column
          field="email"
          header={
            <IconSortTable
              nomeCampo={labelEmail}
              onSortCallback={() =>
                isLoadingUsers ? null : callHandleSort("email")
              }
              iconStatus={
                sortedColumn !== "email"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingUsers ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  <Tooltip
                    target={`${
                      rowData.email.length > 40 ? ".user-email-column" : ""
                    }`}
                    mouseTrack
                    mouseTrackLeft={10}
                  />

                  <div
                    className={`user-email-column ellipsis fit-content`}
                    data-pr-tooltip={rowData.email}
                  >
                    <span>{rowData.email}</span>
                  </div>
                </>
              )
            )
          }
        />
        <Column
          field="role"
          header={
            <IconSortTable
              nomeCampo={labelRuolo}
              onSortCallback={() =>
                isLoadingUsers ? null : callHandleSort("role")
              }
              iconStatus={
                sortedColumn !== "role"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingUsers ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <div className="flex-container justify-between align-center custom-body-table">
                  {getRoleName(rowData.role)}
                  {(currentUserRole === labelSuperAdmin.toUpperCase() ||
                    (currentUserRole === labelAdmin.toUpperCase() &&
                      (rowData.role === 1 || rowData.role === 2))) && (
                    <UsersTableRowButtonActions
                      user={rowData}
                      editCallback={showEditModalDialog}
                    />
                  )}
                </div>
              )
            )
          }
        />
      </DataTable>
      <ModalEditUser
        showModal={showEditModal}
        user={userToEdit!}
        closeModalEditUserCallback={closeModalEditTopic}
      />
    </>
  );
};
